import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { hash, fn } from '@ember/helper';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import {
  HdsTabs,
  HdsIcon,
} from '@hashicorp/design-system-components/components';
import { Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import { TYPE_NETWORK } from 'common/utils/cloud-resource-types';
import {
  AWS_CONNECTIONS_INFO,
  AWS_DISCLOSURE_GUIDES_PEERING,
} from 'networks-common/utils/aws-connections';
import {
  PRODUCT_CONSUL,
  PRODUCT_VAULT,
} from 'networks-common/utils/content-tabs';
import {
  AWS_SECURITY_GROUP,
  AWS_ROUTE_TABLE,
} from 'networks-common/utils/peerings';
import MarkCompleteAll from 'hcp/components/page/hvn-peerings/configuration-accordion/mark-complete-all';
import WebConsole from 'hcp/components/page/hvn-peerings/configuration-accordion/web-console';
import AwsCli from 'hcp/components/page/hvn-peerings/configuration-accordion/aws-cli';

/**
 * PageHvnPeeringsConfigurationAccordion component.
 *
 * @class PageHvnPeeringsConfigurationAccordion
 * @description Accordion for configuring HVN peerings, supporting AWS CLI and Web Console flows.
 *
 * @argument {Object} model - The Model object.
 * @argument {Object} connectionMethods - A string defined in Peering util as constants, representing Web Console or AWS CLI.
 * @argument {String} product - Product such as consul or vault.
 * @argument {Object} peeringMetadata - The peering JSON stored in the Metadata service for this peering.
 *
 * @example
 * <Page::HvnPeerings::ConfigurationAccordion
 *   @peering={{peering}}
 *   @connectionMethods={{connectionMethods}}
 *   @product={{product}}
 *   @peeringMetadata={{peeringMetadata}}
 * />
 */
export default class PageHvnPeeringsConfigurationAccordion extends Component {
  @service api;
  @service('metadata.local-storage') metadata;

  @tracked awsSecurityGroupId = '';

  products = {
    consul: PRODUCT_CONSUL,
    vault: PRODUCT_VAULT,
  };

  configurationCompletionSteps = {
    securityGroup: AWS_SECURITY_GROUP,
    routeTable: AWS_ROUTE_TABLE,
  };

  get isAwsCloudShell() {
    return this.args.connectionMethods.isAwsCloudShell;
  }

  get isWebConsole() {
    return this.args.connectionMethods.isWebConsole;
  }

  awsDisclosureGuides = AWS_DISCLOSURE_GUIDES_PEERING;

  connectionsInfo = AWS_CONNECTIONS_INFO;

  get cidrBlock() {
    let { cidrBlock } = this.args.model.network;
    return cidrBlock;
  }

  get region() {
    let { region } = this.args.model.peering.target.awsTarget;
    return region;
  }

  get providerPeeringId() {
    let { providerPeeringId } = this.args.model.peering;
    return providerPeeringId;
  }

  get securityGroupMarkedComplete() {
    let { peeringMetadata } = this.args;
    return peeringMetadata.completion.includes(
      this.configurationCompletionSteps.securityGroup
    );
  }

  get routeTableMarkedComplete() {
    let { peeringMetadata } = this.args;
    return peeringMetadata.completion.includes(
      this.configurationCompletionSteps.routeTable
    );
  }

  get peeringMarkedComplete() {
    return this.securityGroupMarkedComplete && this.routeTableMarkedComplete;
  }

  markAsComplete = (completedStep) => {
    let {
      model: {
        project: { id: projectId },
        network: { id: networkId },
        peering: { id: peeringId },
      },
      peeringMetadata,
    } = this.args;
    this.saveCompletionMetadata.perform({
      projectId,
      networkId,
      peeringId,
      peeringMetadata,
      completedSteps: [completedStep],
    });
  };

  markAllAsComplete = () => {
    let {
      model: {
        project: { id: projectId },
        network: { id: networkId },
        peering: { id: peeringId },
      },
      peeringMetadata,
    } = this.args;
    let completedSteps = [AWS_SECURITY_GROUP, AWS_ROUTE_TABLE];
    this.saveCompletionMetadata.perform({
      projectId,
      networkId,
      peeringId,
      peeringMetadata,
      completedSteps,
    });
  };

  saveCompletionMetadata = task(
    async ({
      projectId,
      networkId,
      peeringId,
      peeringMetadata,
      completedSteps,
    }) => {
      const scope = 'resource';
      const identifier = `project/${projectId}/${TYPE_NETWORK}/${networkId}`;

      let { completion } = peeringMetadata;

      if (completedSteps.length === 1) {
        let [completedStep] = completedSteps;
        if (!completion.includes(completedStep)) {
          completion = [...completion, completedStep];
        } else {
          completion = completion.filter((step) => step !== completedStep);
        }
      } else {
        let stepsToBeAddedAsComplete = completedSteps.filter(
          (completedStep) => !completion.includes(completedStep)
        );
        if (stepsToBeAddedAsComplete.length > 0) {
          completion = [...completion, ...stepsToBeAddedAsComplete];
        } else {
          completion = [];
        }
      }

      let newPeeringMetadata = {};
      newPeeringMetadata[peeringId] = { ...peeringMetadata, ...{ completion } };

      let d = {
        scope,
        identifier,
        metadata: { peering: newPeeringMetadata },
      };
      await this.metadata.set(identifier, d);
      this.args.updatePeeringMetadata(newPeeringMetadata[peeringId]);
    }
  );

  <template>
    {{#if this.isAwsCloudShell}}
      <AwsCli
        @model={{@model}}
        @connectionMethod={{@connectionMethod}}
        @connection={{hash
          region=this.region
          cidrBlock=this.cidrBlock
          providerPeeringId=this.providerPeeringId
        }}
        @products={{this.products}}
        @product={{@product}}
        @awsDisclosureGuides={{this.awsDisclosureGuides}}
        @displayBadge={{true}}
        @completion={{hash
          securityGroupMarkedComplete=this.securityGroupMarkedComplete
          routeTableMarkedComplete=this.routeTableMarkedComplete
        }}
        @markSecurityGroupAsComplete={{fn
          this.markAsComplete
          this.configurationCompletionSteps.securityGroup
        }}
        @markRouteTableAsComplete={{fn
          this.markAsComplete
          this.configurationCompletionSteps.routeTable
        }}
      />
      <MarkCompleteAll
        @markCompleteAll={{this.markAllAsComplete}}
        @peeringMarkedComplete={{this.peeringMarkedComplete}}
      />
    {{else if this.isWebConsole}}
      <WebConsole
        @model={{@model}}
        @connectionMethod={{@connectionMethod}}
        @connection={{hash
          region=this.region
          cidrBlock=this.cidrBlock
          providerPeeringId=this.providerPeeringId
        }}
        @products={{this.products}}
        @product={{@product}}
        @awsDisclosureGuides={{this.awsDisclosureGuides}}
        @displayBadge={{true}}
        @completion={{hash
          securityGroupMarkedComplete=this.securityGroupMarkedComplete
          routeTableMarkedComplete=this.routeTableMarkedComplete
        }}
        @markSecurityGroupAsComplete={{fn
          this.markAsComplete
          this.configurationCompletionSteps.securityGroup
        }}
        @markRouteTableAsComplete={{fn
          this.markAsComplete
          this.configurationCompletionSteps.routeTable
        }}
      />
      <MarkCompleteAll
        @markCompleteAll={{this.markAllAsComplete}}
        @peeringMarkedComplete={{this.peeringMarkedComplete}}
      />
    {{else}}
      <HdsTabs
        data-configuration-accordion-hds-tabs
        {{boxPadding "lg 0 0"}}
        as |T|
      >
        <T.Tab data-configuration-accordion-aws-cli-tab>
          <Flex @alignItems="center" @gap="2xs">
            <HdsIcon @name="aws-color" @isInline={{true}} />
            <span>
              {{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.title"
              }}
            </span>
          </Flex>
        </T.Tab>
        <T.Tab data-configuration-accordion-web-console-tab>
          <Flex @alignItems="center" @gap="2xs">
            <HdsIcon @name="hashicorp-color" @isInline={{true}} />
            <span>
              {{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.title"
              }}
            </span>
          </Flex>
        </T.Tab>
        <T.Panel data-configuration-accordion-aws-cli-panel>
          <AwsCli
            @model={{@model}}
            @connection={{hash
              region=this.region
              cidrBlock=this.cidrBlock
              providerPeeringId=this.providerPeeringId
            }}
            @products={{this.products}}
            @product={{@product}}
            @awsDisclosureGuides={{this.awsDisclosureGuides}}
          />
        </T.Panel>
        <T.Panel data-configuration-accordion-web-console-panel>
          <WebConsole
            @model={{@model}}
            @connectionMethod={{@connectionMethod}}
            @connection={{hash
              region=this.region
              cidrBlock=this.cidrBlock
              providerPeeringId=this.providerPeeringId
            }}
            @products={{this.products}}
            @product={{@product}}
            @awsDisclosureGuides={{this.awsDisclosureGuides}}
          />
        </T.Panel>
      </HdsTabs>
    {{/if}}
  </template>
}
